/**
 * /followups/:id — Follow-up execution detail (Story 7.14 + 7.14b refactor).
 *
 * Mostra:
 *   - badge de status + timestamp + position no pool
 *   - link para conversation + status da conversa + customer (masked)
 *   - regra aplicada (delay + active flag)
 *   - messageContentSnapshot completo (whitespace-pre-wrap)
 *   - errorMessage destacado quando presente
 *
 * Refactor 7.14b extracted:
 *   - typed client → `lib/followups.ts`
 *   - hook → `useFollowupDetail`
 *   - 404 page customizada com "Voltar para a lista"
 */

import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FollowupStatusBadge } from '@/components/followups/FollowupStatusBadge';
import { useFollowupDetail } from '@/hooks/useFollowups';
import { ApiError } from '@/lib/api';
import { formatDateTime as fmtDateTime } from '@/lib/format-date';

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } };
    return body?.error?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function formatDateTime(iso: string | null): string {
  return fmtDateTime(iso) || '—';
}

export function FollowupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const detailQuery = useFollowupDetail(id);

  if (detailQuery.isLoading) {
    return (
      <div className="space-y-4" data-testid="followup-detail-skeleton">
        <div className="h-8 w-64 animate-pulse rounded bg-muted" />
        <div className="h-48 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (detailQuery.error) {
    const err = detailQuery.error;
    const isNotFound = err instanceof ApiError && err.status === 404;
    return (
      <Card>
        <CardContent className="space-y-3 p-6 text-sm">
          {isNotFound ? (
            <>
              <p className="text-base font-medium">Follow-up não encontrado</p>
              <p className="text-muted-foreground">
                Este follow-up pode ter sido apagado ou nunca existiu.
              </p>
              <Button variant="outline" onClick={() => navigate('/followups')}>
                Voltar para a lista
              </Button>
            </>
          ) : (
            <p className="text-destructive">{humanizeError(err, 'Falha ao carregar follow-up.')}</p>
          )}
        </CardContent>
      </Card>
    );
  }

  const f = detailQuery.data;
  if (!f) return null;

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/followups')} className="-ml-2">
          ← Voltar
        </Button>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Follow-up</h1>
        <div className="mt-2 flex items-center gap-3 text-sm text-muted-foreground">
          <FollowupStatusBadge status={f.status} />
          <span>Tentativa em: {formatDateTime(f.attemptedAt)}</span>
          <span>Pool position #{f.position}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contexto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Conversa:</span>
              <Link
                to={`/conversations/${f.conversationId}`}
                className="font-mono text-xs text-primary hover:underline"
              >
                abrir →
              </Link>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status da conversa:</span>
              <span>{f.conversationStatus}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Campanha:</span>
              <Link to={`/campaigns/${f.campaignId}`} className="text-primary hover:underline">
                {f.campaignName}
              </Link>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cliente:</span>
              <span className="font-mono text-xs">{f.maskedPhone}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Regra aplicada</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Rule ID:</span>
              <span className="font-mono text-xs">{f.followupRule.id.slice(0, 8)}…</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Delay:</span>
              <span>{f.followupRule.delayHours}h</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Regra ativa:</span>
              <span>{f.followupRule.active ? 'sim' : 'não'}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mensagem (snapshot)</CardTitle>
        </CardHeader>
        <CardContent>
          <pre
            className="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-muted/50 p-3 text-sm"
            data-testid="followup-message-snapshot"
          >
            {f.messageContentSnapshot}
          </pre>
          {f.errorMessage ? (
            <div className="mt-4">
              <div className="text-xs text-muted-foreground">Erro:</div>
              <pre className="mt-1 overflow-x-auto rounded bg-destructive/10 p-2 text-xs text-destructive">
                {f.errorMessage}
              </pre>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
